import { isEmpty } from "lodash";
import React, { FC, useEffect } from "react";
import Modal from "react-modal";
import { Provider } from "react-redux";
import { useSelector } from "react-redux";
import {
  useLocation,
  useMatch,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import { Store } from "redux";

import {
  characterEnvActions,
  ConfigUtils,
  Constants,
  generateApiAdapter,
  rulesEngineSelectors,
} from "@dndbeyond/character-rules-engine/es";
import { GameLogContextProvider } from "@dndbeyond/game-log-components";
import { PddContextProvider } from "@dndbeyond/pocket-dimension-dice/components/PddContextProvider";
import { Context as DiceWorkerContext } from "@dndbeyond/pocket-dimension-dice/context";

import config, { authEndpoint } from "~/config";
import { useAuth } from "~/contexts/Authentication";
import { CharacterThemeProvider } from "~/contexts/CharacterTheme";
import { useFeatureFlags } from "~/contexts/FeatureFlag";
import { SidebarProvider } from "~/contexts/Sidebar";
import useUserName from "~/hooks/useUserName";
import useUserRoles from "~/hooks/useUserRoles";

import "../styles/styles.scss";
import CharacterBuilderContainer from "./CharacterBuilder/containers/CharacterBuilderContainer";
import CharacterSheetContainer from "./CharacterSheet/containers/CharacterSheetContainer";
import configureSheetStore from "./CharacterSheet/store/configureStore";
import { appEnvActions } from "./Shared/actions";
import { Managers } from "./Shared/managers";
import {
  DiceFeatureConfigurationState,
  GameLogState,
} from "./Shared/stores/typings";
import {
  AppLoggerUtils,
  AppNotificationUtils,
  ErrorUtils,
} from "./Shared/utils";
import appConfig from "./config";

const BASE_PATHNAME = config.basePathname;

interface Props {
  readOnly: boolean;
  redirect?: string;
  characterId: number | null;
}

export const SheetBuilderApp: React.FC<Props> = ({
  readOnly,
  characterId,
  redirect,
}) => {
  const username = useUserName();
  const user = useAuth();
  const userRoles = useUserRoles();
  const navigate = useNavigate();
  const matchPremadePage = useMatch(`${BASE_PATHNAME}/premade/*`);
  const matchBuilderWithOutCharacter = useMatch(`${BASE_PATHNAME}/builder/*`);
  const matchBuilderWithCharacter = useMatch(
    `${BASE_PATHNAME}/:characterId/builder/*`
  );
  const matchSheet = useMatch(`${BASE_PATHNAME}/:characterId/`);
  const matchSheetWithShareId = useMatch(
    `${BASE_PATHNAME}/:characterId/:shareId`
  );
  const currentPath = useLocation().pathname;
  const [searchParams] = useSearchParams();
  const isVttView = searchParams.get("view") === "vtt";

  if (config === undefined) {
    throw Error("Missing Config");
  }

  let appContext: Constants.AppContextTypeEnum | null = null;
  switch (true) {
    case !!(
      matchBuilderWithOutCharacter ||
      matchBuilderWithCharacter ||
      matchPremadePage
    ):
      appContext = Constants.AppContextTypeEnum.BUILDER;
      break;
    case !!(matchSheet || matchSheetWithShareId):
      appContext = Constants.AppContextTypeEnum.SHEET;
      break;
    default:
      throw new Error("Invalid app context");
  }

  const diceFeatureConfiguration: DiceFeatureConfigurationState = {
    enabled: true,
    menu: true,
    assetBaseLocation: config.diceAssetEndpoint,
    apiEndpoint: config.diceApiEndpoint,
    trackingId: config.analyticTrackingId,
  };

  ErrorUtils.initReporting({
    debug: appConfig.debug,
  });

  let store: Store = configureSheetStore();
  store.dispatch({
    type: "SET_ROUTE_CONTROLS",
    payload: { navigate, currentPath },
  });

  let parsedDiceEnabled: boolean = true;
  if (!diceFeatureConfiguration.enabled) {
    parsedDiceEnabled = false;
  } else {
    // if local storage is disabled it will just crash when trying to access, this prevents crashing.
    try {
      const diceEnabled = localStorage.getItem("dice-enabled");
      // once dice are enabled set the default to on;
      parsedDiceEnabled = diceEnabled === "true" || diceEnabled === null;
    } catch (e) {}
  }

  let lastMessageTime = 0;
  try {
    const lsLastMessageTime = localStorage.getItem(
      `gameLog-lastMessageTime-${characterId}`
    );
    if (lsLastMessageTime) {
      lastMessageTime = parseInt(lsLastMessageTime);
    }
  } catch (e) {}

  const gameLogSettings: GameLogState = {
    isOpen: false,
    lastMessageTime: lastMessageTime,
    apiEndpoint: config.gameLogApiEndpoint,
    ddbApiEndpoint: config.ddbApiEndpoint,
  };

  ConfigUtils.configureRulesEngine({
    apiAdapter: generateApiAdapter(config.authEndpoint, readOnly),
    apiInfo: config.apiInfo,
    store,
    onLogMessage: AppLoggerUtils.logMessage,
    onLogError: AppLoggerUtils.logError,
    onNotification: AppNotificationUtils.dispatchNotification,
    includeCustomItems: true,
    canUseCurrencyContainers: true,
  });

  store.dispatch(
    characterEnvActions.dataSet({
      context: appContext,
    })
  );

  store.dispatch(
    appEnvActions.dataSet({
      username,
      userId: user?.id ? Number(user.id) : -1,
      userRoles,
      characterId,
      authEndpoint: config.authEndpoint,
      redirect,
      diceEnabled: parsedDiceEnabled,
      diceFeatureConfiguration: diceFeatureConfiguration,
      gameLog: gameLogSettings,
    })
  );

  // Setup axe-core in development, not in production
  if (process.env.NODE_ENV !== "production") {
    // axe(React, ReactDOM, 10000);
  }

  Modal.setAppElement("#character-tools-target");

  // If the view is the VTT view, set --top-navigation-height to 0 because the top nav will not be present.
  useEffect(() => {
    if (isVttView) {
      document.body.style.setProperty("--top-navigation-height", "0px");
    }
  }, []);

  return (
    <Provider store={store}>
      <Managers>
        <CharacterThemeProvider>
          <Content
            gameLogSettings={gameLogSettings}
            diceFeatureConfiguration={diceFeatureConfiguration}
            characterId={characterId}
          />
        </CharacterThemeProvider>
      </Managers>
    </Provider>
  );
};

interface ContentProps {
  gameLogSettings: GameLogState;
  diceFeatureConfiguration: DiceFeatureConfigurationState;
  characterId: number | null;
}

const Content: FC<ContentProps> = ({
  gameLogSettings,
  diceFeatureConfiguration,
  characterId,
}) => {
  const user = useAuth();
  const { tacticalMapsShared3dDiceFlag } = useFeatureFlags();
  const isUserEmpty = isEmpty(user);
  const matchBuilderWithOutCharacter = useMatch(`${BASE_PATHNAME}/builder/*`);
  const matchBuilderWithCharacter = useMatch(
    `${BASE_PATHNAME}/:characterId/builder/*`
  );
  const matchPremadePage = useMatch(`${BASE_PATHNAME}/premade/*`);
  const campaignId = useSelector(rulesEngineSelectors.getCampaign)?.id;

  return (
    <PddContextProvider
      logger={console.log}
      errorCallback={() => {}}
      nodeEnv={process.env.NODE_ENV}
      env={config.environment}
      user={isUserEmpty ? undefined : user}
      canUseShared3dDice={tacticalMapsShared3dDiceFlag}
      gameId={campaignId ? String(campaignId) : undefined}
    >
      <GameLogContextProvider
        baseUrl={gameLogSettings.apiEndpoint}
        ddbApiUrl={gameLogSettings.ddbApiEndpoint}
        diceServiceUrl={diceFeatureConfiguration.apiEndpoint}
        authUrl={authEndpoint}
        diceThumbnailsUrl={`${diceFeatureConfiguration.assetBaseLocation}/images/thumbnails`}
        entityId={characterId ? characterId.toString() : ""}
        diceWorkerContext={DiceWorkerContext}
        canUseShared3dDice={tacticalMapsShared3dDiceFlag}
      >
        <SidebarProvider>
          {matchBuilderWithOutCharacter ||
          matchBuilderWithCharacter ||
          matchPremadePage ? (
            <CharacterBuilderContainer isPremadeListing={!!matchPremadePage} />
          ) : (
            <CharacterSheetContainer authEndpoint={config.authEndpoint} />
          )}
        </SidebarProvider>
      </GameLogContextProvider>
    </PddContextProvider>
  );
};
